import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('gauss_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('gauss_token');
      localStorage.removeItem('gauss_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// Dashboard API
export const dashboardAPI = {
  getBestSellers: () => api.get('/dashboard/best-sellers'),
  getSummary: () => api.get('/dashboard/summary'),
};

// Orders API
export const ordersAPI = {
  getAll: () => api.get('/orders'),
  getById: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  updateStatus: (id, status) => api.put(`/orders/${id}/status?status=${status}`),
  delete: (id) => api.delete(`/orders/${id}`),
  syncQogita: () => api.get('/orders/qogita/sync'),
};

// ASIN Database API
export const asinDatabaseAPI = {
  getAll: (params = {}) => api.get('/asin-database', { params }),
  create: (data) => api.post('/asin-database', data),
  deleteAll: (params = {}) => api.delete('/asin-database', { params }),
  uploadLabel: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/asin-database/${id}/label`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadLabel: (id) => api.get(`/asin-database/${id}/label`, { responseType: 'blob' }),
  deleteLabel: (id) => api.delete(`/asin-database/${id}/label`),
  update: (id, data) => api.put(`/asin-database/${id}`, data),
  delete: (id) => api.delete(`/asin-database/${id}`),
  lookup: (ean) => api.get(`/asin-database/lookup/${ean}`),
  importCsv: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/asin-database/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Warehouse API
export const warehouseAPI = {
  getStock: () => api.get('/warehouse/stock'),
  updateStock: (data) => api.post('/warehouse/stock', data),
  adjustStock: (productId, adjustment) => api.put(`/warehouse/stock/${productId}/adjust?adjustment=${adjustment}`),
  setStockQuantity: (productId, quantity) => api.put(`/warehouse/stock/${productId}`, { quantity }),
  deleteStockEntry: (productId) => api.delete(`/warehouse/stock/${productId}`),
  addStockManual: (data) => api.post('/warehouse/stock/manual', data),
  getReceived: (date) => api.get(`/warehouse/received${date ? `?date=${date}` : ''}`),
  addReceived: (data) => api.post('/warehouse/received', data),
  updateReceived: (id, data) => api.put(`/warehouse/received/${id}`, data),
  deleteReceived: (id) => api.delete(`/warehouse/received/${id}`),
};

// Planner API
export const plannerAPI = {
  getPlan: () => api.get('/planner'),
};

// Shipments API
export const shipmentsAPI = {
  getAll: () => api.get('/shipments'),
  getById: (id) => api.get(`/shipments/${id}`),
  create: (data) => api.post('/shipments', data),
  cancel: (id) => api.post(`/shipments/${id}/cancel`),
  uploadPdf: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/shipments/${id}/upload-pdf`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getFiles: (id) => api.get(`/shipments/${id}/files`),
};

// Settings API
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// Seed Data API
export const seedAPI = {
  seedData: () => api.post('/seed-data'),
};

export default api;
